import { call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import AuthService from '../services/auth.service';
import * as actionType from './actionTypes';
import { API_ENDPOINTS, buildURL, httpRequest, SnackbarHandlerSchema } from '../utils';
import {
  changeLanguuageAction,
  changeMenuItemsAction,
  checkIfOnboardDeviceIsValidAction,
  getRolesAction,
  handleLoginSideEffectAction,
  isLoginSuccessAction,
  logoutUserAction,
  onboardFormSubmitAction,
  publishErrorAction,
  redirectToHomePageBasisRoleAction,
  resetAppDataAction,
  resetPasswordAction,
  setUserDataAction,
  sideMenuPopulatorAction,
  validateTokenAction,
} from './actions';
import {
  DEV_DATA,
  GATEWAY_ROLE_LANDING_PAGE_MAP,
  ROLE_LANDING_PAGE_MAP,
  SNACKBAR_VARIANTS,
  SUPERVISOR_MONITOR_ROLE_GROUP,
  WATCHUSER_ROLE_GROUP,
} from '../utils/constants';
import {
  GATEWAY_USER_SIDE_MENU_OPTIONS,
  GUEST_SIDE_MENU_OPTIONS,
  USER_SIDE_MENU_OPTIONS,
  USERROLE_SIDEBAR_OPTIONS_GATEWAY_MAP,
  USERROLE_SIDEBAR_OPTIONS_MAP,
} from '../utils/NavBar.options';
import { getOnboardDeviceDeviceIdSelector, getOnboardDeviceSupervisroIddSelector, getRolesSelector, getUserSelector, isLoggedInSelector } from './selectors';
import { getAllNotificationsAction } from '../views/Notifications/actions';
import { changeLocale } from '../components/LanguageProvider/actions';
import { USER_TYPES } from '../views/Users/constants';
import USER_ROLES from '../utils/UserRoles';
import { getAllBltEntitiesAction } from '../views/BLTGateway/actions';
import { getSelectedDeviceSelector } from '../views/Devices/selectors';

export function* handleLogout() {
  const auth = new AuthService();
  auth.unsetToken();
  yield put(resetAppDataAction.Trigger());
  yield put(resetAppDataAction.Completed());
  yield put(sideMenuPopulatorAction.Trigger({ loggedIn: false }));
  yield put(logoutUserAction.Success());
}

export function* handleValidateToken(action) {
  yield put(validateTokenAction.Reset());
  yield put(validateTokenAction.Loading(true));

  const { token, redirect } = action.payload;
  // TODO - Remove Dev  WA
  let validateTokenResponse = {};
  if (token === DEV_DATA.context.token) {
    validateTokenResponse.success = true;
    validateTokenResponse.context = DEV_DATA.context;
  } else {
    validateTokenResponse = yield call(httpRequest.get, API_ENDPOINTS.VALIDATE_TOKEN_API_URL);
  }

  if (validateTokenResponse.success) {
    const { user } = validateTokenResponse.context;
    yield put(handleLoginSideEffectAction.Trigger({ user, token }));
    if (redirect) {
      yield put(redirectToHomePageBasisRoleAction.Trigger({user}));
    }
  } else {
    const auth = new AuthService();
    auth.unsetToken();
    yield put(validateTokenAction.Failure(validateTokenResponse.message));
    yield put(push('/auth/login'));
  }
  yield put(validateTokenAction.Loading(false));
  yield put(validateTokenAction.Success());
}

export function* handlePasswordResetSaga(action) {
  yield put(resetPasswordAction.Loading(true));

  const { oldPassword, newPassword } = action.payload;

  const resetPasswordResponse = yield call(httpRequest.post, API_ENDPOINTS.RESET_PASSWORD_URL, {
    oldPassword,
    newPassword,
  });

  if (resetPasswordResponse.success) {
    yield put(resetPasswordAction.Success());
    const successSnackbarObj = new SnackbarHandlerSchema(200, 'Password Reset Successful', SNACKBAR_VARIANTS.SUCCESS);
    yield put(publishErrorAction.Trigger(successSnackbarObj));
  } else {
    yield put(resetPasswordAction.Failure());
  }

  yield put(resetPasswordAction.Loading(false));
}

function* getRolesSaga() {
  yield put(getRolesAction.Loading(true));

  const getRolesResponse = yield call(httpRequest.get, API_ENDPOINTS.GET_ROLES_URL);

  if (getRolesResponse.success) {
    const { roles } = getRolesResponse.context || {};
    const rolesObj = {};
    Object.keys(roles).forEach(roleLabel => {
      rolesObj[roles[roleLabel]] = roleLabel.toUpperCase();
    });
    yield put(getRolesAction.Success(rolesObj));
  } else {
    yield put(getRolesAction.Failure(getRolesResponse.message));
  }

  yield put(getRolesAction.Loading(false));
}

function* setSideMenuOptionsSaga() {
  let sideMenuOptions = GUEST_SIDE_MENU_OPTIONS;
  const loggedIn = yield select(isLoggedInSelector);
  if (loggedIn) {
    const user = yield select(getUserSelector);
    const { role } = user;
    const roles = yield select(getRolesSelector);
    const roleName = roles[role] || '';
    if (user.userType === USER_TYPES.GATEWAY) {
      sideMenuOptions = USERROLE_SIDEBAR_OPTIONS_GATEWAY_MAP[roleName] || GATEWAY_USER_SIDE_MENU_OPTIONS
    } else {
      sideMenuOptions = USERROLE_SIDEBAR_OPTIONS_MAP[roleName] || USER_SIDE_MENU_OPTIONS;
    }
  }
  yield put(changeMenuItemsAction.Completed(sideMenuOptions));
}

function* handleLoginSideEffectSaga(action) {
  const { user, token } = action.payload;
  yield put(getRolesAction.Request());
  yield put(isLoginSuccessAction.Completed());
  yield put(setUserDataAction.Completed({ user, token }));
  if (user.selectedLanguage) {
    yield put(changeLocale(user.selectedLanguage));
  }

  yield put(handleLoginSideEffectAction.Completed());
}

function* setNotifIntervalSaga() {
  let isLoggedIn = true;

  while (isLoggedIn) {
    isLoggedIn = yield select(isLoggedInSelector);
    const loggedInUser = yield select(getUserSelector);
    const roles = yield select(getRolesSelector);

    const userRole = (roles || {})[loggedInUser.role];

    if (isLoggedIn && [...WATCHUSER_ROLE_GROUP, ...SUPERVISOR_MONITOR_ROLE_GROUP].includes(userRole)) {
      yield put(getAllNotificationsAction.Request());
    }

    if (userRole === USER_ROLES.ADMIN || loggedInUser.userType === USER_TYPES.GATEWAY) {
      yield put(getAllBltEntitiesAction.Request());
    }
    yield delay(60000);
  }
}

function* redirectToHomePageBasedOnRole(action) {
  const { user } = action.payload;
  const landingMap = user.userType === USER_TYPES.GATEWAY ? GATEWAY_ROLE_LANDING_PAGE_MAP : ROLE_LANDING_PAGE_MAP;
  const path =
    user.role !== undefined
      ? landingMap[user.role] || landingMap.default
      : landingMap.default;
  yield put(push(path));
}

function* changeLanguageSelectionSaga(action) {
  yield put(changeLanguuageAction.Loading(true));

  const language = action.payload;

  const resetPasswordResponse = yield call(httpRequest.post, API_ENDPOINTS.CHANGE_LANGUGAE_DETAILS, {
    selectedLanguage: language
  });

  if (resetPasswordResponse.success) {
    yield put(changeLanguuageAction.Success());
    yield put(changeLocale(language));
    const successSnackbarObj = new SnackbarHandlerSchema(200, 'Change Language Successful', SNACKBAR_VARIANTS.SUCCESS);
    yield put(publishErrorAction.Trigger(successSnackbarObj));
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  } else {
    yield put(changeLanguuageAction.Failure());
  }

  yield put(changeLanguuageAction.Loading(false));
}

function* checkOnboardDeviceValidSaga(action) {
  yield put(checkIfOnboardDeviceIsValidAction.Loading(true));
  const deviceId = yield select(getOnboardDeviceDeviceIdSelector)

  const url = buildURL(API_ENDPOINTS.CHECK_DEVICE_VALID_FOR_ONBOARDING, {deviceId})
  const checkValidResponse = yield call(httpRequest.get, url)

  if (checkValidResponse.success) {
    const response = checkValidResponse.context
    yield put(checkIfOnboardDeviceIsValidAction.Success(response))
  } else {
    yield put(checkIfOnboardDeviceIsValidAction.Failure());
  }

  yield put(checkIfOnboardDeviceIsValidAction.Loading(false));
}

function* deviceOnboardFormSubmitSaga(action) {
  yield put(onboardFormSubmitAction.Loading(true));
  const deviceUUID = yield select(getOnboardDeviceDeviceIdSelector)
  const supervisorId = yield select(getOnboardDeviceSupervisroIddSelector)
  const data = {...action.payload, deviceUUID, supervisorId}
  const checkValidResponse = yield call(httpRequest.post, API_ENDPOINTS.DEVICE_ONBOARD_FORM_SUBMIT, data)

  if (checkValidResponse.success) {
    const response = checkValidResponse.context
    const successSnackbarObj = new SnackbarHandlerSchema(200, 'Device Onboarding Successful', SNACKBAR_VARIANTS.SUCCESS);
    yield put(publishErrorAction.Trigger(successSnackbarObj));
    yield put(onboardFormSubmitAction.Success(response))
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  } else {
    yield put(onboardFormSubmitAction.Failure());
  }

  yield put(onboardFormSubmitAction.Loading(false));
}

function* deviceOnboardingSendEmailSaga(action) {
  yield put(onboardFormSubmitAction.Loading(true));
  const selectedDevice = yield select(getSelectedDeviceSelector)
  const data = {deviceUUID: selectedDevice.uuid, emailAddress: action.payload, baseURL: window.location.origin}
  const checkValidResponse = yield call(httpRequest.post, API_ENDPOINTS.DEVICE_ONBOARD_SEND_EMAIL, data)

  if (checkValidResponse.success) {
    const response = checkValidResponse.context
    const successSnackbarObj = new SnackbarHandlerSchema(200, 'Email Sent Successfully', SNACKBAR_VARIANTS.SUCCESS);
    yield put(publishErrorAction.Trigger(successSnackbarObj));
    yield put(onboardFormSubmitAction.Success(response))
  } else {
    yield put(onboardFormSubmitAction.Failure());
  }

  yield put(onboardFormSubmitAction.Loading(false));
}

export default function* appPageSaga() {
  yield takeLatest(actionType.LOGGED_OUT_ACTIONTYPE.REQUEST, handleLogout);
  yield takeLatest(actionType.VALIDATE_TOKEN_ACTIONTYPE.REQUEST, handleValidateToken);
  yield takeLatest(actionType.RESET_PASSWORD_ACTIONTYPE.REQUEST, handlePasswordResetSaga);
  yield takeLatest(actionType.GET_ROLES_ACTIONTYPE.REQUEST, getRolesSaga);
  yield takeLatest(actionType.HANDLE_LOGIN_SIDEEFFECT_ACTIONTYPE.TRIGGER, handleLoginSideEffectSaga);
  yield takeLatest(actionType.CHANGE_LANGUAGE_ACTIONTYPE.REQUEST, changeLanguageSelectionSaga);
  yield takeLatest(actionType.CHECK_ONBOARD_DEVICE_VALID_ACTIONTYPE.REQUEST, checkOnboardDeviceValidSaga)
  yield takeLatest(actionType.DEVICE_ONBOARD_FORM_SUBMIT_ACTIONTYPE.REQUEST, deviceOnboardFormSubmitSaga)
  yield takeLatest(actionType.SEND_ONBOARD_EMAIL_ACTIONTYPE.REQUEST, deviceOnboardingSendEmailSaga)

  // All posiible scenarios whewre side menu options need to change i.e on Login, on Logout, on Role Change
  yield takeLatest(actionType.GET_ROLES_ACTIONTYPE.SUCCESS, setSideMenuOptionsSaga);
  yield takeLatest(actionType.HANDLE_LOGIN_SIDEEFFECT_ACTIONTYPE.COMPLETED, setSideMenuOptionsSaga);
  yield takeLatest(actionType.LOGGED_OUT_ACTIONTYPE.SUCCESS, setSideMenuOptionsSaga);
  yield takeLatest(actionType.SET_NOTIF_INTERVAL_ACTIONTYPE.TRIGGER, setNotifIntervalSaga);
  yield takeLatest(actionType.REDIRECT_TO_HOMEPAGE_BASED_ROLE_ACTIONTYPE.TRIGGER, redirectToHomePageBasedOnRole)
}
